import { SEO } from '@/components/SEO';
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import LocationFAQ from "@/components/locations/LocationFAQ";
import LocationBlogLinks from "@/components/locations/LocationBlogLinks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Droplets, Sparkles, Home, Filter, Brush, Sun } from "lucide-react";
import { Link } from "react-router-dom";

const FillmorePage = () => {
  const navigate = useNavigate();

  const locationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ventura Power Washing - Fillmore",
    "description": "Professional pressure washing, window cleaning, and carpet cleaning services in Fillmore, CA. Serving residential and commercial properties in the Fillmore area.",
    "url": "https://www.venturapowerwashing.com/fillmore",
    "telephone": "(805) 464-7166",
    "areaServed": {
      "@type": "City",
      "name": "Fillmore",
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 34.3992,
      "longitude": -118.9181
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Fillmore",
      "addressRegion": "CA",
      "postalCode": "93015",
      "addressCountry": "US"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "07:00",
      "closes": "19:00"
    },
    "priceRange": "$$",
    "image": "https://www.venturapowerwashing.com/og-image.jpg",
    "sameAs": [
      "https://www.facebook.com/venturapowerwashing",
      "https://www.instagram.com/venturacleaningco",
      "https://www.yelp.com/biz/ventura-power-washing-thousand-oaks"
    ]
  };

  const services = [
    {
      icon: <Droplets className="h-8 w-8 text-ventura-blue" />,
      title: "Pressure Washing",
      description: "Deep cleaning for driveways, patios, decks, and exterior surfaces.",
      link: "/services/pressure-washing"
    },
    {
      icon: <Sparkles className="h-8 w-8 text-ventura-blue" />,
      title: "Window Cleaning",
      description: "Crystal-clear windows for homes and businesses.",
      link: "/services/window-washing"
    },
    {
      icon: <Home className="h-8 w-8 text-ventura-blue" />,
      title: "Carpet Cleaning",
      description: "Professional carpet and upholstery cleaning services.",
      link: "/services/carpet-cleaning"
    },
    {
      icon: <Filter className="h-8 w-8 text-ventura-blue" />,
      title: "Gutter Cleaning",
      description: "Keep gutters clear and protect your home from water damage.",
      link: "/services/gutter-cleaning"
    },
    {
      icon: <Brush className="h-8 w-8 text-ventura-blue" />,
      title: "Soft Wash",
      description: "Gentle cleaning for delicate surfaces like stucco and painted exteriors.",
      link: "/services/soft-wash"
    },
    {
      icon: <Sun className="h-8 w-8 text-ventura-blue" />,
      title: "Solar Panel Cleaning",
      description: "Maximize solar efficiency with professional panel maintenance.",
      link: "/services/solar-panel-cleaning"
    }
  ];

  const faqs = [
    {
      question: "How much does pressure washing cost in Fillmore, CA?",
      answer: "Most driveway, patio, and house pressure washing jobs in Fillmore run $150-$400, depending on square footage and surface type. We provide a free, no-obligation estimate before any work begins."
    },
    {
      question: "How much does window cleaning cost in Fillmore?",
      answer: "Residential window cleaning in Fillmore typically costs $150-$300 for a standard home, inside and out. Historic Victorian homes with multi-pane windows may fall at the higher end of that range."
    },
    {
      question: "Do you offer carpet cleaning in Fillmore?",
      answer: "Yes. Whole-house carpet cleaning in Fillmore generally runs $100-$300 depending on square footage and the number of rooms. We use deep extraction methods that handle dust from surrounding citrus groves and agricultural land."
    },
    {
      question: "Is solar panel cleaning worth it for Fillmore properties?",
      answer: "Yes — Fillmore's agricultural dust and citrus pollen settle on panels quickly and can reduce efficiency by 15-25%. Professional solar panel cleaning in Fillmore typically costs $150-$350 and helps maximize your energy production."
    }
  ];

  return (
    <>
      <SEO
        title="Pressure Washing & Cleaning Services in Fillmore, CA | Ventura Power Washing"
        description="Professional pressure washing, window cleaning, and carpet cleaning services in Fillmore, CA. Serving 93015 and 93016 zip codes. Call (805) 464-7166 for a free estimate!"
        keywords="pressure washing Fillmore, window cleaning Fillmore CA, carpet cleaning Fillmore, power washing 93015, exterior cleaning Fillmore"
        path="/fillmore"
      >
        <script type="application/ld+json">
          {JSON.stringify(locationSchema)}
        </script>
      </SEO>

      <Navbar />
      
      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-24">
          <Breadcrumbs 
            items={[
              { label: "Home", href: "/" },
              { label: "Fillmore", href: "/fillmore" }
            ]} 
          />
        </div>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-ventura-blue/10 to-ventura-green/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Professional Cleaning Services in <span className="text-ventura-blue">Fillmore, CA</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                Trusted pressure washing, window cleaning, and carpet cleaning services for Fillmore's historic homes and agricultural properties. Serving 93015 and 93016 zip codes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-ventura-blue hover:bg-ventura-blue/90 text-white"
                  onClick={() => window.location.href = 'tel:+18054647166'}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call (805) 464-7166
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-ventura-blue text-ventura-blue hover:bg-ventura-blue/10"
                  onClick={() => navigate('/free-estimate')}
                >
                  Get Free Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
              Our Services in Fillmore
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="mb-4">{service.icon}</div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link 
                      to={service.link}
                      className="text-ventura-blue hover:text-ventura-blue/80 font-medium inline-flex items-center"
                    >
                      Learn More →
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Local Area Information */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-foreground mb-6">
                What Makes Fillmore Properties Unique to Clean?
              </h2>
              <p className="text-muted-foreground mb-6">
                Fillmore's citrus ranch properties, historic Victorian homes, and agricultural buildings face orchard dust and aging
                exterior materials rather than coastal salt air, so they need a different cleaning approach than Ventura's beach
                cities. We proudly serve all neighborhoods in Fillmore, including Downtown Fillmore, North Fillmore, the Historic
                District, and properties along Highway 126.
              </p>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Areas We Serve</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Downtown Fillmore</li>
                    <li>• North Fillmore</li>
                    <li>• Historic District</li>
                    <li>• Highway 126 Corridor</li>
                    <li>• Citrus Grove Properties</li>
                    <li>• Rural Fillmore</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Why Choose Us</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Experience with historic home preservation</li>
                    <li>• Agricultural property cleaning expertise</li>
                    <li>• Eco-friendly cleaning solutions</li>
                    <li>• Same-day service available</li>
                    <li>• 100% satisfaction guarantee</li>
                    <li>• Licensed and insured</li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Button 
                  size="lg"
                  className="bg-ventura-blue hover:bg-ventura-blue/90 text-white"
                  onClick={() => navigate('/free-estimate')}
                >
                  Get Your Free Fillmore Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        <LocationFAQ city="Fillmore" faqs={faqs} />
        <LocationBlogLinks city="Fillmore" />
      </main>

      <Footer />
    </>
  );
};

export default FillmorePage;
